import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './DelinquencyNotifications.module.scss';
import { PageContainer } from '../../components/pageContainer/PageContainer';
import moment from 'moment';
import { Button, Modal, Form, Input } from 'semantic-ui-react';
import { Icon } from '../../components/icon/Icon';
import { DelinquencyModal } from './DelinquencyModal';
import { axios } from '../../services/axiosService';

export const DelinquencyNotifications = () => {
    const [delinquencyNotifications, setDelinquencyNotifications] = useState(
        [],
    );
    const [verifiedData, setVerifiedData] = useState<any[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [sendNotificationLoading, setSendNotificationLoading] =
        useState(false);
    const [disableModalSendNotification, setDisableModalSendNotification] =
        useState(false);
    const [paidOnOrAfterDate, setPaidOnOrAfterDate] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const getDelinquencyNotifications = useCallback(async () => {
        const response = await axios.get('/notifications/delinquencies');
        setDelinquencyNotifications(response.data);
    }, []);

    useEffect(() => {
        getDelinquencyNotifications();
    }, [getDelinquencyNotifications]);

    const verifyDelinquencyData = useCallback(async (document: string) => {
        setSendNotificationLoading(true);
        const response = await axios.post(
            '/notifications/delinquencies/verify',
            {
                delinquencyData: document,
            },
        );

        setVerifiedData(response.data);
        setDisableModalSendNotification(false);
        setModalOpen(true);
        setSendNotificationLoading(false);
    }, []);

    const submitNotifications = useCallback(async () => {
        // /notifications/delinquencies

        setNotificationsLoading(true);
        await axios.post('/notifications/delinquencies', {
            delinquencyData: verifiedData,
            ...(paidOnOrAfterDate && { paidOnOrAfterDate }),
        });

        setNotificationsLoading(false);
        setModalOpen(false);
        setPaidOnOrAfterDate('');
        getDelinquencyNotifications();
    }, [verifiedData, paidOnOrAfterDate, getDelinquencyNotifications]);

    const previewEmail = useCallback(async () => {
        if (!verifiedData || verifiedData.length === 0) {
            return;
        }

        setPreviewLoading(true);
        try {
            // Use the first record as an example for the preview
            const exampleData = verifiedData[0];
            const response = await axios.post(
                '/notifications/delinquencies/preview',
                {
                    amount: exampleData.delinquentAmount,
                    address: exampleData.address,
                    shutoffDate: exampleData.shutoffDate,
                    ...(paidOnOrAfterDate && { paidOnOrAfterDate }),
                },
                {
                    responseType: 'text',
                },
            );

            // Open the preview in a new window using blob URL
            const blob = new Blob([response.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error previewing email:', error);
        } finally {
            setPreviewLoading(false);
        }
    }, [verifiedData, paidOnOrAfterDate]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];

            // read the file
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result;
                if (content) {
                    const fileContent = content.toString();
                    verifyDelinquencyData(fileContent);
                }
            };

            reader.readAsText(file);

            fileInputRef.current!.value = '';
        }
    };

    return (
        <PageContainer>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="large"
                aria-labelledby="delinquency-modal-header"
            >
                <Modal.Header>
                    <span id="delinquency-modal-header">Delinquency Notification Summary</span>
                    <div style={{ float: 'right' }}>
                        <Icon
                            icon="x"
                            color="black"
                            size="large"
                            onClick={() => setModalOpen(false)}
                            aria-label="Close dialog"
                        />
                    </div>
                </Modal.Header>
                <Modal.Content>
                    <DelinquencyModal
                        onClose={() => {
                            setModalOpen(false);
                        }}
                        selectedFileContent={verifiedData}
                    />
                    {!disableModalSendNotification && (
                        <div style={{ marginTop: '20px' }}>
                            <Form>
                                <Form.Field>
                                    <label htmlFor="paid-on-or-after-date">
                                        Payment Date (optional)
                                        <br />
                                        <small style={{ fontWeight: 'normal' }}>
                                            This date will appear in the email as
                                            "If you have paid your full
                                            delinquency amount on or after
                                            [date], please disregard this
                                            notice."
                                        </small>
                                    </label>
                                    <Input
                                        id="paid-on-or-after-date"
                                        placeholder="e.g., 12/15/25"
                                        value={paidOnOrAfterDate}
                                        onChange={(e) =>
                                            setPaidOnOrAfterDate(e.target.value)
                                        }
                                    />
                                </Form.Field>
                            </Form>
                            <div
                                style={{
                                    marginTop: '20px',
                                    display: 'flex',
                                    gap: '10px',
                                    justifyContent: 'center',
                                }}
                            >
                                <Button
                                    onClick={previewEmail}
                                    loading={previewLoading}
                                    secondary
                                >
                                    Preview Email
                                </Button>
                                <Button
                                    onClick={() => {
                                        submitNotifications();
                                    }}
                                    loading={notificationsLoading}
                                    primary
                                >
                                    Send Notifications
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal.Content>
            </Modal>

            <div className={styles.buttonHeader}>
                <Button
                    onClick={handleButtonClick}
                    loading={sendNotificationLoading}
                >
                    Send Notification
                </Button>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    aria-label="Upload delinquency notification file"
                    id="delinquency-file-input"
                />
            </div>
            <div>
                <div className={styles.action_container}>
                    <h1 className={styles.title}>Delinquency Notifications</h1>

                    <table>
                        <caption className="visually-hidden">Delinquency notification history</caption>
                        <thead>
                            <tr className={styles.tableHeader}>
                                <th scope="col" className={styles.alertEnglish}>
                                    Batch Identifier
                                </th>
                                <th scope="col" className={styles.alertSpanish}>
                                    Users Reached
                                </th>
                                <th scope="col" className={styles.alertEnglish}>Sent</th>
                                <th scope="col" className={styles.actions}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {delinquencyNotifications?.map(
                                (notification: any, index) => (
                                    <tr
                                        className={`listItemColor ${styles.item}`}
                                        key={notification.Id}
                                    >
                                        <td className={styles.alertText}>
                                            <span className={styles.alertHeader}>
                                                {notification.Id}
                                            </span>
                                        </td>
                                        <td className={styles.alertText}>
                                            <span className={styles.alertHeader}>
                                                {notification.processed}
                                            </span>
                                        </td>
                                        <td className={styles.timestamp}>
                                            {moment(notification.createdAt)
                                                .tz('America/Chicago')
                                                .format('MM-DD-YY h:mm A')}
                                        </td>
                                        <td className={styles.actions}>
                                            <Button
                                                loading={false}
                                                disabled={false}
                                                onClick={() => {
                                                    setVerifiedData(
                                                        JSON.parse(
                                                            notification.data,
                                                        ),
                                                    );
                                                    setDisableModalSendNotification(
                                                        true,
                                                    );
                                                    setModalOpen(true);
                                                }}
                                            >
                                                View Details
                                            </Button>
                                        </td>
                                    </tr>
                                ),
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </PageContainer>
    );
};
